"use client";

import { Suspense, useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { bladPasskeya } from "@/app/components/SekcjaLogowania";

/**
 * Ekran logowania (ADR-0022, ADR-0059). Jedyna strona publiczna. Passkey zawsze, Google tylko gdy instancja
 * ma klucz Google i adres właściciela. Gdy nikt nie może wejść — pierwsze uruchomienie z kodem startowym z logu.
 */

const KOMUNIKATY: Record<string, string> = {
  AccessDenied: "To konto Google nie ma dostępu do Synthesis.",
  CredentialsSignin: "Nie rozpoznano passkeya. Użyj passkeya dodanego w tej instancji.",
  Configuration: "Błąd konfiguracji logowania.",
  Verification: "Link wygasł. Spróbuj zalogować się ponownie.",
  Zablokowano: "Zbyt wiele nieudanych prób z tego adresu. Logowanie zablokowane na godzinę.",
};

interface StanLogowania {
  pierwszeUruchomienie: boolean;
  google: boolean;
}

/**
 * Sesja może wygasnąć, gdy apka jest otwarta w kafelku — bramka (ADR-0023) przekierowuje wtedy ramkę na ten
 * ekran. Google nie loguje w iframe, a passkey w ramce bywa zablokowany, więc logowanie przenosimy na całe okno
 * (ten sam origin). Powrót = adres okna (powłoka z paskiem), nie goła apka z ramki.
 */
function przeniesZRamki(): boolean {
  if (window.self === window.top || !window.top) return false;
  const { pathname, search } = window.top.location;
  const powrot = pathname.startsWith("/login") ? "/" : `${pathname}${search}`;
  window.top.location.href = `/login?callbackUrl=${encodeURIComponent(powrot)}`;
  return true;
}

const zadanie = (dane: object) =>
  fetch("/api/auth/passkey", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dane),
  });

async function tekstBledu(r: Response, domyslny: string): Promise<string> {
  return (await r.text().catch(() => "")) || domyslny;
}

function TrescLogowania() {
  const parametry = useSearchParams();
  const bladZAdresu = parametry.get("error");
  // Tylko ścieżka względna — nie wypuszczamy użytkownika na obcy adres.
  const powrot = parametry.get("callbackUrl") ?? "/";
  const callbackUrl = powrot.startsWith("/") && !powrot.startsWith("//") ? powrot : "/";

  const [stan, setStan] = useState<StanLogowania | null>(null);
  const [blad, setBlad] = useState<string | null>(
    bladZAdresu ? (KOMUNIKATY[bladZAdresu] ?? "Logowanie się nie powiodło.") : null,
  );
  const [info, setInfo] = useState<string | null>(null);
  const [trwa, setTrwa] = useState(false);
  const [kodWyslany, setKodWyslany] = useState(false);
  const [kod, setKod] = useState("");
  const [nazwa, setNazwa] = useState("");

  useEffect(() => {
    void fetch("/api/auth/passkey")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: StanLogowania | null) => setStan(d ?? { pierwszeUruchomienie: false, google: false }));
  }, []);

  async function zalogujPasskeyem(cel = callbackUrl) {
    if (przeniesZRamki()) return;
    setBlad(null);
    setTrwa(true);
    try {
      const opcje = await zadanie({ akcja: "opcje-logowania" });
      if (!opcje.ok) throw new Error(await tekstBledu(opcje, "Serwer nie wydał wyzwania."));
      const odpowiedz = await startAuthentication({ optionsJSON: await opcje.json() });
      const wynik = await signIn("passkey", { odpowiedz: JSON.stringify(odpowiedz), redirect: false, callbackUrl: cel });
      if (wynik && !wynik.error) {
        window.location.href = cel;
        return;
      }
      setBlad(KOMUNIKATY[wynik?.error ?? ""] ?? "Logowanie się nie powiodło.");
    } catch (e) {
      setBlad(bladPasskeya(e));
    } finally {
      setTrwa(false);
    }
  }

  function zalogujGoogle() {
    if (przeniesZRamki()) return;
    void signIn("google", { callbackUrl });
  }

  async function pokazKod() {
    setBlad(null);
    const r = await zadanie({ akcja: "kod" });
    if (r.ok) setKodWyslany(true);
    else setBlad(await tekstBledu(r, "Nie udało się wygenerować kodu."));
  }

  async function utworzPasskey() {
    if (przeniesZRamki()) return;
    setBlad(null);
    setTrwa(true);
    try {
      const opcje = await zadanie({ akcja: "opcje-rejestracji", kod });
      if (!opcje.ok) throw new Error(await tekstBledu(opcje, "Kod nie został przyjęty."));
      const odpowiedz = await startRegistration({ optionsJSON: await opcje.json() });
      const r = await zadanie({ akcja: "rejestruj", kod, nazwa, odpowiedz });
      if (!r.ok) throw new Error(await tekstBledu(r, "Nie udało się zapisać passkeya."));
      setStan((s) => ({ pierwszeUruchomienie: false, google: s?.google ?? false }));
      setInfo("Passkey dodany. Potwierdź logowanie na urządzeniu.");
    } catch (e) {
      setBlad(bladPasskeya(e));
      setTrwa(false);
      return;
    }
    // Od razu logowanie i kreator (ADR-0060 §1); odmowa urządzenia zostawia zwykły przycisk logowania.
    await zalogujPasskeyem("/start");
  }

  return (
    <main className="login-ekran">
      <section className="login-panel hud-frame hud-brackets hud-scanline">
        <p className="login-nadtytul">SYNTHESIS</p>
        <h1 className="login-tytul">{stan?.pierwszeUruchomienie ? "Pierwsze uruchomienie" : "Dostęp ograniczony"}</h1>
        <p className="login-opis">
          {stan?.pierwszeUruchomienie
            ? "Utwórz passkey właściciela. Kod jednorazowy pojawi się w logu serwera (np. docker logs z kontenera Synthesis) i jest ważny 15 minut."
            : "Ta instancja jest prywatna. Wchodzi tylko właściciel: passkeyem albo kontem Google."}
        </p>

        {blad && (
          <p className="login-blad" role="alert">
            {blad}
          </p>
        )}
        {info && <p className="login-opis">{info}</p>}

        {stan?.pierwszeUruchomienie ? (
          <div className="login-akcje">
            {!kodWyslany ? (
              <button type="button" className="login-przycisk" onClick={() => void pokazKod()}>
                Pokaż kod w logu serwera
              </button>
            ) : (
              <>
                <input
                  className="login-pole"
                  placeholder="kod z logu"
                  autoComplete="one-time-code"
                  value={kod}
                  onChange={(e) => setKod(e.target.value)}
                />
                <input
                  className="login-pole"
                  placeholder="nazwa urządzenia, np. Telefon"
                  maxLength={40}
                  value={nazwa}
                  onChange={(e) => setNazwa(e.target.value)}
                />
                <button
                  type="button"
                  className="login-przycisk"
                  disabled={trwa || !kod.trim() || !nazwa.trim()}
                  onClick={() => void utworzPasskey()}
                >
                  {trwa ? "Czekam na urządzenie…" : "Utwórz passkey"}
                </button>
              </>
            )}
          </div>
        ) : (
          stan && (
            <div className="login-akcje">
              <button type="button" className="login-przycisk" disabled={trwa} onClick={() => void zalogujPasskeyem()}>
                {trwa ? "Czekam na urządzenie…" : "Zaloguj passkeyem"}
              </button>
              {stan.google && (
                <button type="button" className="login-przycisk drugi" onClick={zalogujGoogle}>
                  Zaloguj przez Google
                </button>
              )}
            </div>
          )
        )}
      </section>
    </main>
  );
}

export default function StronaLogowania() {
  // `useSearchParams` wymaga granicy Suspense — bez niej Next przerywa build.
  return (
    <Suspense fallback={null}>
      <TrescLogowania />
    </Suspense>
  );
}
